/**
 * Cold archive — the wire contract between the runner's archiver (apps/sandbox
 * services/archive) and the server that brokers its Storage (workspace-archive.service.ts).
 *
 * What an archive is: the due top-level entries of ONE archivable root (lifetimes.ts
 * `archiveAfterDays`), streamed `tar czf - -C <dirname(root)> <basename(root)>/<entry>…`
 * with no temp file on the volume, cut into parts of at most WORKSPACE_ARCHIVE_PART_BYTES,
 * each PUT through its own server-minted signed URL. Restore is the parts, concatenated in
 * order, into `tar xzf - -C <workspace>/<dirname(root)>`.
 *
 * FLOW (the runner holds no Storage credentials; every route is user-scoped by its token):
 *   1. POST …/workspace-archive/upload-url { archiveId, part }   → { uploadUrl, key }, per part
 *   2. POST …/workspace-archive/verify { archiveId, parts }       → the server compares each
 *      stored object's size to the bytes streamed
 *   3. the box reads the parts back in order, each through a URL minted just before it
 *      (POST …/workspace-archive/part-url { archiveId, part }): sha256 per part, and `tar t`
 *      counts entries
 *   4. POST …/workspace-archive/record { archiveId, parts, entries, sourceBytes } (the root is
 *      read off the id: archiveRootOf)
 *   5. only then the entries are removed from the box, and POST …/workspace-archive/removed
 *   Any failure: POST …/workspace-archive/discard, which deletes every part and raises one
 *   alert per (user, root) episode. The root stays whole.
 * Record and discard share ONE row per archive id: whichever lands first decides, and the
 * other answers 409 (a discard after a record deletes nothing; a record after a discard is
 * refused). POST …/workspace-archive/settle { root } closes a root's failure episode when the
 * box finds the root empty.
 */

import { ARCHIVABLE_ROOTS } from './lifetimes.js';

/** The private bucket archives live in (created by 20270101090000_workspace_archives.sql). */
export const WORKSPACE_ARCHIVES_BUCKET = 'workspace-archives';

/**
 * The largest part. The bucket's own file_size_limit is 600 MiB, under staging's 1 GB project
 * cap (prod's is 3 GB), so no archive is refused for its size: a bigger one is more parts.
 */
export const WORKSPACE_ARCHIVE_PART_BYTES = 512 * 1024 * 1024;

/** 32 GiB of gzip: past any volume a plan buys, so hitting it means something is wrong. */
export const WORKSPACE_ARCHIVE_MAX_PARTS = 64;

/** `<root-slug>-<yyyy-mm-dd>-<8 hex>`: the object stem under `<userId>/`. */
export const WORKSPACE_ARCHIVE_ID_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*-\d{4}-\d{2}-\d{2}-[0-9a-f]{8}$/;

/** '.vestra/legacy' → 'vestra-legacy', 'lost+found' → 'lost-found'. */
export function archiveRootSlug(root: string): string {
  return root
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** A fresh archive id for `root`: `<slug>-<yyyy-mm-dd>-<hex8>`, the date in UTC. */
export function newArchiveId(root: string, nowMs: number, hex8: string): string {
  return `${archiveRootSlug(root)}-${new Date(nowMs).toISOString().slice(0, 10)}-${hex8}`;
}

/** The archivable root an archive id names, or undefined for any id that names none. */
export function archiveRootOf(archiveId: string): string | undefined {
  if (!WORKSPACE_ARCHIVE_ID_RE.test(archiveId)) return undefined;
  const slug = archiveId.slice(0, -'-yyyy-mm-dd-xxxxxxxx'.length);
  return ARCHIVABLE_ROOTS.find((r) => archiveRootSlug(r.root) === slug)?.root;
}

/** The object key of one part; `part` counts from 1. */
export function archivePartKey(userId: string, archiveId: string, part: number): string {
  return `${userId}/${archiveId}.tar.gz.part-${String(part).padStart(4, '0')}`;
}

/** One stored part, in order: what the record lists and what restore re-hashes. */
export interface WorkspaceArchivePart {
  key: string;
  bytes: number;
  sha256: string;
}

/**
 * Where an archive failed. `interrupted` = a previous attempt died mid-flight (the next pass
 * cleans it); `deadline` = the attempt ran out of the time its hold on the box allows.
 */
export type WorkspaceArchiveStage = 'upload' | 'too_large' | 'size' | 'readback' | 'record' | 'interrupted' | 'deadline';

export interface WorkspaceArchiveUploadUrlResponse {
  uploadUrl: string;
  key: string;
}

export type WorkspaceArchiveVerifyResponse = { ok: true } | { ok: false; mismatch: string };

/** One part's download URL, minted just before the box reads it (a pass lives 15 minutes). */
export interface WorkspaceArchivePartUrlResponse {
  url: string;
}

export interface WorkspaceArchiveRestoreResponse {
  root: string;
  entries: number;
  parts: Array<WorkspaceArchivePart & { url: string }>;
}
